/**
 * Centralized Auth Redirect Utilities
 * Single source of truth for all auth redirects to prevent loops
 */

// Allowed returnTo prefixes (same-origin, safe paths only)
const SAFE_RETURN_TO_PREFIXES = ['/admin', '/en', '/fr', '/agent', '/portal', '/profile', '/settings', '/.lovable/oauth/consent'];

// Auth routes that should redirect authenticated users away
const AUTH_ROUTES = [
  '/en/auth',
  '/en/auth/login',
  '/en/auth/signup',
  '/en/auth/sign-in',
  '/en/auth/sign-up',
  '/portal/login',
  '/portal/register'
];

/**
 * Validates if a returnTo path is safe to redirect to
 * Prevents open redirect vulnerabilities
 */
export function isValidReturnTo(path: string | null | undefined): boolean {
  if (!path) return false;
  
  // Must be a relative path (starts with /)
  if (!path.startsWith('/')) return false;
  
  // Prevent protocol-relative URLs
  if (path.startsWith('//')) return false;
  
  // Must not be an auth route (prevent redirect loops)
  if (AUTH_ROUTES.some(route => path.startsWith(route))) return false;
  
  // Must match one of the safe prefixes
  return SAFE_RETURN_TO_PREFIXES.some(prefix => path.startsWith(prefix));
}

/**
 * Sanitizes and validates a returnTo URL
 * Returns the path if valid, null otherwise
 */
export function sanitizeReturnTo(url: string | null | undefined): string | null {
  if (!url) return null;
  
  try {
    // Handle full URLs by extracting path
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const parsed = new URL(url);
      // Only accept same-origin URLs
      if (parsed.origin !== window.location.origin) return null;
      url = parsed.pathname + parsed.search;
    }
    
    return isValidReturnTo(url) ? url : null;
  } catch {
    return null;
  }
}

/**
 * Gets the returnTo from URL params or location state
 */
export function getReturnToFromContext(
  searchParams?: URLSearchParams,
  locationState?: { from?: { pathname: string } }
): string | null {
  // Check URL params first
  const paramReturnTo = searchParams?.get('returnTo') || searchParams?.get('redirect');
  if (paramReturnTo) {
    return sanitizeReturnTo(paramReturnTo);
  }
  
  // Check location state
  const stateReturnTo = locationState?.from?.pathname;
  return sanitizeReturnTo(stateReturnTo || null);
}

/**
 * Builds a login redirect URL with returnTo parameter
 */
export function buildLoginRedirect(returnTo?: string): string {
  const base = '/en/auth/login';
  const sanitized = sanitizeReturnTo(returnTo || null);
  
  if (sanitized) {
    return `${base}?returnTo=${encodeURIComponent(sanitized)}`;
  }
  
  return base;
}

/**
 * Role-based default redirect paths
 */
export type AppRole = 'client' | 'agent' | 'manager' | 'admin';

export interface AuthInfo {
  is_internal?: boolean;
  portal_type?: 'internal' | 'client' | 'none';
  effective_role?: AppRole;
}

export function getDefaultRedirectForRole(
  role: AppRole | null,
  authInfo?: AuthInfo | null
): string {
  // Internal staff always goes to admin
  if (authInfo?.is_internal) {
    return '/admin/dashboard';
  }
  
  switch (role) {
    case 'admin':
    case 'manager':
      return '/admin/dashboard';
    case 'agent':
      return '/en/agent';
    case 'client':
      return '/en/portal';
    default:
      // Fallback based on portal type
      if (authInfo?.portal_type === 'client') return '/en/portal';
      if (authInfo?.portal_type === 'internal') return '/admin/dashboard';
      return '/admin/dashboard';
  }
}

/**
 * Determines the final redirect path after auth
 * Prioritizes safe returnTo, falls back to role-based redirect
 */
export function getPostAuthRedirect(
  role: AppRole | null,
  authInfo?: AuthInfo | null,
  returnTo?: string | null
): string {
  const sanitizedReturnTo = sanitizeReturnTo(returnTo);
  
  if (sanitizedReturnTo) {
    return sanitizedReturnTo;
  }
  
  // Use effective_role from authInfo if role is null
  const effectiveRole = role || authInfo?.effective_role || null;
  
  return getDefaultRedirectForRole(effectiveRole, authInfo);
}

/**
 * Checks if current path is an auth route
 */
export function isAuthRoute(path: string): boolean {
  return AUTH_ROUTES.some(route => path.startsWith(route));
}

/**
 * Check if user should be redirected from auth pages
 * (authenticated users visiting login/signup)
 */
export function shouldRedirectFromAuthPage(
  isAuthenticated: boolean,
  currentPath: string
): boolean {
  return isAuthenticated && isAuthRoute(currentPath);
}
